import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { whyChooseUs, approachSteps } from '../data/mock';
import { Award, Users, Wrench, Leaf, MapPin, Headphones as HeadphonesIcon, Cpu, ShieldCheck, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';

const iconMap = { Award, Users, Wrench, Leaf, MapPin, HeadphonesIcon, Cpu, ShieldCheck };

export default function WhyDurga() {
  return (
    <div>
      <PageHero
        title="Why organisations choose Durga."
        subtitle="Quality, sustainability and technology — the three pillars behind over 30 years of measurable outcomes."
        image="/assets/food-beverage.png"
        breadcrumbs={[{ label: 'Why Durga' }]}
      />

      {/* Pillars */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                Icon: ShieldCheck,
                title: 'Quality',
                image: '/assets/mechanised-housekeeping.jpg',
                imgFit: 'cover',
                imgBg: undefined,
                points: [
                  'Proven track record of placing the right skills in the right roles.',
                  'Deep understanding of the market and the skills in demand.',
                  'End-to-end support: job descriptions, interviews, onboarding.',
                ],
              },
              {
                Icon: Leaf,
                title: 'Sustainability',
                image: '/assets/horticulture.jpg',
                imgFit: 'cover',
                imgBg: undefined,
                points: [
                  'Green-building aligned processes across every service.',
                  'Energy, water and chemical footprint continuously reduced.',
                  'Waste management and healthier, eco-friendly spaces.',
                ],
              },
              {
                Icon: Sparkles,
                title: 'Technology',
                image: '/assets/drone-facade-cleaning.jpg',
                imgFit: 'contain',
                imgBg: '#0A2540',
                points: [
                  'Drone facade cleaning, ride-on scrubbers, HEPA vacuums and PPE.',
                  'Trained operators for every piece of equipment.',
                  'Digital audits, checklists and reporting.',
                ],
              },
            ].map(({ Icon, title, image, imgFit, imgBg, points }) => (
              <div key={title} className="group rounded-2xl overflow-hidden bg-slate-50 border border-slate-100 hover:shadow-xl transition-all">
                <div className="aspect-[16/10] overflow-hidden" style={imgBg ? { background: imgBg } : undefined}>
                  <img src={image} alt={title} style={{ objectFit: imgFit || 'cover' }} className="w-full h-full group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="w-12 h-12 rounded-xl bg-[#0A2540] text-white flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">{title}</h3>
                  <ul className="space-y-2.5">
                    {points.map((p) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-slate-600 leading-relaxed">
                        <CheckCircle2 size={16} className="text-[#F5B800] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-[1300px] mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Six reasons organisations pick us</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight">Built for accountability at scale.</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyChooseUs.map((w) => {
              const Icon = iconMap[w.icon];
              return (
                <div key={w.title} className="bg-white p-7 rounded-2xl border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 text-[#0A2540] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{w.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Our Approach</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight">A predictable four-step engagement.</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachSteps.map((a) => (
              <div key={a.step} className="relative bg-slate-50 rounded-2xl p-7 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="text-5xl font-extrabold text-slate-200 mb-3 leading-none">{a.step}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{a.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/contact" className="inline-flex items-center gap-2 btn-cta px-7 py-3.5 rounded-lg font-semibold transition-colors">
              Start with a Free Audit <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
